// Dataphyll Crew in Safari, for iPhones while the App Store listing is in
// review: Tagless harvest only (pages/CrewWebPage.tsx). The picker signs in,
// clocks on with signal once, and from then on shows their tag QR with or
// without signal. The receipter's scan of the tag is what puts the clock-on on
// Timesheets (api services/crew-clock.js ingestTagClockOn), so nothing here is
// uploaded after the clock-on.
//
// The clock-on QR and the tag are read and written the way the Crew app does:
// the shuffle is byte-exact with api services/crew-qr.js, and the tag with the
// app's lib/core/worker_tag.dart.

import qrcode from 'qrcode-generator'

export const CREW_QR_PREFIX = 'https://control.dataphyll.com/crewapp?x='

// ---- the clock-on QR (crew-qr.js, without its signatures) -----------------

const MBIG = 2147483647
const MSEED = 161803398

// .NET's new Random(seed).Next(0, max), as crew-qr.js has it.
function dotNetRandom(seed: number) {
  const s = new Array(56).fill(0)
  let mj = (MSEED - Math.abs(seed)) % MBIG
  if (mj < 0) mj += MBIG
  s[55] = mj
  let mk = 1
  for (let i = 1; i < 55; i++) {
    const ii = (21 * i) % 55
    s[ii] = mk
    mk = mj - mk
    if (mk < 0) mk += MBIG
    mj = s[ii]
  }
  for (let k = 1; k <= 4; k++) {
    for (let i = 1; i < 56; i++) {
      s[i] -= s[1 + ((i + 30) % 55)]
      if (s[i] < 0) s[i] += MBIG
    }
  }
  let inext = 0
  let inextp = 21
  return (max: number) => {
    inext = inext === 55 ? 1 : inext + 1
    inextp = inextp === 55 ? 1 : inextp + 1
    let r = s[inext] - s[inextp]
    if (r === MBIG) r -= 1
    if (r < 0) r += MBIG
    s[inext] = r
    return Math.trunc(r * (1.0 / MBIG) * max)
  }
}

export function unshuffle(shuffled: string) {
  const chars = shuffled.replace(/\+/g, ' ').split('')
  const next = dotNetRandom(259)
  const swaps = chars.map(() => next(chars.length))
  for (let i = chars.length - 1; i >= 0; i--) {
    const t = chars[swaps[i]]
    chars[swaps[i]] = chars[i]
    chars[i] = t
  }
  return chars.join('')
}

export interface ClockOnQr {
  url: string
  tenantId: string
  teamId: string
  teamName: string
  siteName: string
  expires: number
  // mode=tagless, clockoff=17:00, induct=1, ...
  settings: Record<string, string>
}

// What the phone can tell from a clock-on QR with no signal: its team and its
// settings. Whether it is genuine and still current is the server's to say at
// the clock-on; null for anything that is not a clock-on QR.
export function readClockOnQr(url: string): ClockOnQr | null {
  if (!url.startsWith(CREW_QR_PREFIX)) return null
  const sections = unshuffle(url.slice(CREW_QR_PREFIX.length)).split('$')
  const h = sections[0].split('~')
  if (h[0] !== 'DC2' || h.length !== 9 || sections.length < 2) return null
  const settings: Record<string, string> = {}
  for (const pair of sections[1].split('~')) {
    const i = pair.indexOf('=')
    if (i > 0) settings[pair.slice(0, i)] = pair.slice(i + 1)
  }
  const expires = Number(h[8])
  return { url, tenantId: h[1].toLowerCase(), teamId: h[2].toLowerCase(), teamName: h[4], siteName: h[5], expires, settings }
}

// A clock-on QR scanned with the iPhone camera opens /crewapp?x=..., which
// index.html sends on to /crew with the same query. The text is not
// URL-encoded, so it is read raw: a '+' in it is a '+', not a space.
export function qrFromSearch(search: string): string | null {
  if (!search.startsWith('?x=')) return null
  try {
    return CREW_QR_PREFIX + decodeURIComponent(search.slice(3))
  } catch {
    return null
  }
}

export const normaliseCode = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, '')

// When a shift that began at `startMs` ends: the next time the phone's clock
// reads the team's clock-off (crew-clock.js shiftEndsAfter does the same in the
// site's zone).
export function shiftEndsAfter(startMs: number, clockoff: string | undefined) {
  const m = /^([01][0-9]|2[0-3]):([0-5][0-9])$/.exec(clockoff || '') || [, '23', '00']
  const end = new Date(startMs)
  end.setHours(Number(m[1]), Number(m[2]), 0, 0)
  if (end.getTime() <= startMs) end.setDate(end.getDate() + 1)
  return end.getTime()
}

// ---- the tag (worker_tag.dart) ---------------------------------------------

const NET_EPOCH_TICKS = 621355968000000000n

// .NET ticks of the LOCAL wall clock, as the app's netTicks writes them.
export function netTicks(d: Date) {
  const wallMs = d.getTime() - d.getTimezoneOffset() * 60000
  return (BigInt(wallMs) * 10000n + NET_EPOCH_TICKS).toString()
}

export const TAG_VALID_MS = 60 * 1000
const MAX_NAME = 40

const clean = (v: string) => v.replace(/~/g, '').trim()
const cleanName = (v: string) => {
  const s = v.replace(/~/g, ' ').replace(/\s+/g, ' ').trim()
  return s.length > MAX_NAME ? s.slice(0, MAX_NAME).trimEnd() : s
}

// The QR holds only printable ASCII: a name in another script (瑞仙罗, Thai)
// and `%` travel percent-escaped, as the app's worker_tag.dart writes them and
// api routes/crew.js tagName reads them. qrcode-generator's Byte mode keeps
// only each character's low byte, so 瑞仙 罗 went out as `^Ù W`, which no
// receipter could read.
const escapeName = (v: string) => v.replace(/[^ -~]|%/gu, (c) => encodeURIComponent(c))

// `DTU{user_id}~{valid_till}~{shift_start}~{team_id}~{name}`, valid for a minute
// from `now`.
export function encodeTag(t: { humanId: string; teamId: string; shiftStartMs: number; name: string }, now = new Date()) {
  const till = new Date(now.getTime() + TAG_VALID_MS)
  return `DTU${clean(t.humanId)}~${netTicks(till)}~${netTicks(new Date(t.shiftStartMs))}~${clean(t.teamId)}~${escapeName(cleanName(t.name))}`
}

// The QR as rows of '1' (dark) and '0'. Level M, as the app draws a tag.
export function qrRows(text: string) {
  const q = qrcode(0, 'M')
  q.addData(text, 'Byte')
  q.make()
  const n = q.getModuleCount()
  const rows: string[] = []
  for (let r = 0; r < n; r++) {
    let row = ''
    for (let c = 0; c < n; c++) row += q.isDark(r, c) ? '1' : '0'
    rows.push(row)
  }
  return rows
}

// ---- what the phone keeps --------------------------------------------------

export interface CrewWebShift {
  tenantId: string
  teamId: string
  teamName: string
  siteName: string
  humanId: string
  name: string
  shiftStartMs: number
  endsAtMs: number
}

export interface CrewWebStore {
  uid: string
  // The person this sign-in is in each workspace, from its clock-ons.
  humans: Record<string, string>
  // Typed code -> team's QR (GET /api/crew/teams), so a code works offline.
  codes: Record<string, string>
  // The shift this phone is clocked on to, if any.
  shift: CrewWebShift | null
  // Clock-ons made with no signal, to send once there is some.
  deferred: DeferredClockOn[]
  // Clock off presses not yet taken by Control (POST /api/crew/web-clock-off).
  clockOffs: QueuedClockOff[]
}

// A press of Clock off: the shift it ends (its workspace, team and the shift
// start its clock-on sent as shift_ticks) and when. Sent after the deferred
// clock-ons, so its clock-on is there first; kept until Control takes it.
export interface QueuedClockOff {
  tenantId: string
  teamId: string
  shiftTicks: string
  at: number
}

const isClockOff = (v: unknown): v is QueuedClockOff => {
  const c = v as QueuedClockOff | null
  return !!c && typeof c.tenantId === 'string' && !!c.tenantId && typeof c.teamId === 'string' && !!c.teamId
    && typeof c.shiftTicks === 'string' && /^\d{1,19}$/.test(c.shiftTicks) && typeof c.at === 'number'
}

// Clock off: the shift ends on the phone now, and its clock-off is queued.
export const withClockOff = (s: CrewWebStore, at = Date.now()): CrewWebStore =>
  s.shift
    ? {
        ...s,
        shift: null,
        clockOffs: [
          ...s.clockOffs,
          { tenantId: s.shift.tenantId, teamId: s.shift.teamId, shiftTicks: netTicks(new Date(s.shift.shiftStartMs)), at },
        ].slice(-MAX_DEFERRED),
      }
    : s

// Sends each queued clock-off, oldest first, and says which are still to
// send: no signal, a retry (202: its clock-on has not reached Control yet),
// or an answer to try later. One refused for good, or too old, is dropped.
export async function sendClockOffs(list: QueuedClockOff[], post: (c: QueuedClockOff) => Promise<unknown>, now = Date.now()): Promise<QueuedClockOff[]> {
  const left: QueuedClockOff[] = []
  for (const c of [...list].sort((a, b) => a.at - b.at)) {
    if (now - c.at > DEFERRED_MAX_MS) continue
    try {
      const r = (await post(c)) as { ack?: string } | null
      if (r?.ack === 'retry') left.push(c)
    } catch (e) {
      const status = (e as { status?: number }).status
      if (!status || status === 401 || status === 426 || status === 429 || status >= 500) left.push(c)
    }
  }
  return left
}

// A clock-on started on the phone with no signal: the team's QR and when it
// was scanned. Sent as a deferred clock-on (POST /api/crew/clock-on with
// deferred: true), so it reaches Timesheets even when no receipter scans the
// worker's tag; one Control has from a tag scan already merges with it.
export interface DeferredClockOn {
  qr: string
  at: number
}

// Control takes a deferred clock-on up to 3 days after its scan
// (MAX_DEFERRED_SCAN_AGE_SECS); an older one would be put at that limit, so
// it is dropped. An hour is left for the send itself.
export const DEFERRED_MAX_MS = (3 * 86400 - 3600) * 1000
const MAX_DEFERRED = 20

const isDeferred = (v: unknown): v is DeferredClockOn => {
  const d = v as DeferredClockOn | null
  return !!d && typeof d.qr === 'string' && !!d.qr && typeof d.at === 'number'
}

export const withDeferred = (s: CrewWebStore, d: DeferredClockOn): CrewWebStore => ({ ...s, deferred: [...s.deferred, d].slice(-MAX_DEFERRED) })

// Sends each deferred clock-on, oldest first, and says which are still to
// send: those that met no signal, or an answer saying to try later (a sign-in
// to refresh, Control busy). One Control refused for good, or one too old to
// send, is dropped.
export async function sendDeferred(list: DeferredClockOn[], post: (d: DeferredClockOn) => Promise<unknown>, now = Date.now()): Promise<DeferredClockOn[]> {
  const left: DeferredClockOn[] = []
  for (const d of [...list].sort((a, b) => a.at - b.at)) {
    if (now - d.at > DEFERRED_MAX_MS) continue
    try {
      await post(d)
    } catch (e) {
      const status = (e as { status?: number }).status
      if (!status || status === 401 || status === 426 || status === 429 || status >= 500) left.push(d)
    }
  }
  return left
}

const KEY = 'dataphyll.crewWeb'

// A shift the tag can be drawn from: a person and a team. One kept without
// them (a clock-on answered by something other than Control) is dropped, as
// the tag would fail to draw every time it was opened.
export const usableShift = (v: unknown): v is CrewWebShift => {
  const s = v as CrewWebShift | null
  return !!s && typeof s.humanId === 'string' && !!s.humanId && typeof s.teamId === 'string' && !!s.teamId
    && typeof s.shiftStartMs === 'number' && typeof s.endsAtMs === 'number'
}

export function loadStore(uid: string): CrewWebStore {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || 'null')
    if (s && s.uid === uid) {
      const humans = Object.fromEntries(Object.entries(s.humans || {}).filter(([, v]) => typeof v === 'string' && v))
      const deferred = Array.isArray(s.deferred) ? s.deferred.filter(isDeferred) : []
      const clockOffs = Array.isArray(s.clockOffs) ? s.clockOffs.filter(isClockOff) : []
      return { codes: {}, ...s, humans: humans as Record<string, string>, shift: usableShift(s.shift) ? s.shift : null, deferred, clockOffs }
    }
  } catch {
    // Storage blocked or garbled: start empty.
  }
  return { uid, humans: {}, codes: {}, shift: null, deferred: [], clockOffs: [] }
}

export function saveStore(s: CrewWebStore) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s))
  } catch {
    // Private browsing: the page still works until it is closed.
  }
}
